import React, { useEffect, useState } from "react";
import { getTranslateByKey } from "@/main/services/utils";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { CustomError } from "@/main/services/class/customError";
import { SpecialAccountApi } from "@/main/api/specialAccountApi";
import { SpecialAccountRequest } from "@/main/models/request";
import useAsyncModalConfirmReason from "../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

import * as S from "./styles";
import KogInputTextControlledNew from "../inputText/inputTextControlledNew";
import SecondaryButton from "../button/secondaryButton";
import { KeyValue } from "@/main/models/common";
import KogInputAutoCompleteCheckControlled from "../inputAutoCompleteCheck/inputAutoCompleteCheckControlled";
import KogInputPercentageControlled from "../inputPercentage/inputPercentage";
import { KOGAreaButtonsModal } from "../styles";

export interface ISpecialAccountForm {
	guidInsurer: string;
	guid?: string;
	name: string;
	agentKey: string;
	totalLossPercentage: string;
	users: Array<{
		key: string;
		value: string;
	}>;
}

const INITIAL_FORM_STATE: ISpecialAccountForm = {
	guidInsurer: "",
	guid: "",
	name: "",
	agentKey: "",
	totalLossPercentage: "",
	users: [],
};

export function normalizeTotalLossPercentage(value: string | null | undefined): number | null {
	if (value === null || value === undefined || value.trim() === "") {
		return null;
	}

	const parsed = Number(value);
	return Number.isNaN(parsed) ? null : parsed;
}

interface ISpecialAccountsModal {
	guidInsurer: string;
	hasEdit: boolean;
	setHasEdit: React.Dispatch<React.SetStateAction<boolean>>;
	specialAccountEdit: {
		guidInsurer: string;
		guid?: string;
		name: string;
		agentKey: string;
		totalLossPercentage?: number | null;
		users: Array<{
			guidSpecialAccountUser?: string;
			idUser: string;
			name: string;
		}>;
	} | null;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	usersList: KeyValue[] | null;
	reloadData: () => void;
}

function SpecialAccountsModal({
	guidInsurer,
	hasEdit,
	setHasEdit,
	specialAccountEdit,
	setOpen,
	usersList,
	reloadData,
}: ISpecialAccountsModal) {
	const { setValue, handleSubmit, clearErrors, control } = useForm<ISpecialAccountForm>({
		mode: "onChange",
		defaultValues: {
			...INITIAL_FORM_STATE,
			guidInsurer: guidInsurer,
		},
	});

	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	const specialAccountApi = new SpecialAccountApi();

	function resetValueForm() {
		setValue("guidInsurer", guidInsurer);
		setValue("guid", "");
		setValue("name", "");
		setValue("agentKey", "");
		setValue("totalLossPercentage", "");
		setValue("users", []);
	}

	useEffect(() => {
		if (hasEdit && specialAccountEdit) {
			setHasEdit(true);
			setValue("guidInsurer", guidInsurer);
			setValue("guid", specialAccountEdit.guid);
			setValue("name", specialAccountEdit.name);
			setValue("agentKey", specialAccountEdit.agentKey);
			setValue(
				"totalLossPercentage",
				specialAccountEdit.totalLossPercentage != null ? String(specialAccountEdit.totalLossPercentage) : "",
			);

			const usersFormatted = specialAccountEdit.users.map((user) => ({
				key: user.idUser,
				value: user.name,
			}));

			setValue("users", usersFormatted);
			clearErrors();
		} else {
			resetValueForm();
		}
	}, [hasEdit, specialAccountEdit, guidInsurer]);

	async function handleSubmitSpecialAccount(data: ISpecialAccountForm) {
		let resultConfirm = null;
		let filesRequest = undefined;

		resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setLoadingSave(true);

		try {
			const request: SpecialAccountRequest = {
				guidInsurer: data.guidInsurer,
				name: data.name,
				agentKey: data.agentKey,
				totalLossPercentage: normalizeTotalLossPercentage(data.totalLossPercentage),
				users: data.users.map((user) => ({
					idUser: user.key,
					name: user.value,
				})),
			};

			request.changeJustification = {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			};

			await specialAccountApi.saveScpecialAccount(request);

			reloadData();
			toast.success(getTranslateByKey("information.specialAccountSaveSuccessfully"));
			setOpen(false);
			resetValueForm();
			setHasEdit(false);
		} catch (error) {
			if (error instanceof CustomError) {
				toast.error(error.message);
			}
		} finally {
			setLoadingSave(false);
		}
	}

	async function handleSubmitEditSpecialAccount(data: ISpecialAccountForm) {
		let resultConfirm = null;
		let filesRequest = undefined;

		resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setLoadingSave(true);

		try {
			const request: SpecialAccountRequest = {
				guid: specialAccountEdit?.guid || "",
				guidInsurer: data.guidInsurer,
				name: data.name,
				agentKey: String(data.agentKey),
				totalLossPercentage: normalizeTotalLossPercentage(data.totalLossPercentage),
				users: data.users.map((user) => ({
					guidSpecialAccountUser: specialAccountEdit?.users.find((u) => u.idUser === user.key)?.guidSpecialAccountUser,
					idUser: user.key,
					name: user.value,
				})),
			};

			request.changeJustification = {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			};

			await specialAccountApi.saveScpecialAccount(request);

			reloadData();
			toast.success(getTranslateByKey("information.specialAccountEditSuccessfully"));
			resetValueForm();
			setHasEdit(false);
			setOpen(false);
		} catch (error) {
			if (error instanceof CustomError) {
				toast.error(error.message || getTranslateByKey("information.specialAccountEditError"));
			} else {
				toast.error(getTranslateByKey("information.specialAccountEditError"));
			}
		} finally {
			setLoadingSave(false);
		}
	}

	return (
		<>
			<S.HeaderText>
				{getTranslateByKey(hasEdit ? "information.specialAccountsEdit" : "information.specialAccountsCreate")}
			</S.HeaderText>
			<form onSubmit={handleSubmit(hasEdit ? handleSubmitEditSpecialAccount : handleSubmitSpecialAccount)} noValidate>
				<S.AreaForm>
					<KogInputTextControlledNew
						name="name"
						label={getTranslateByKey("information.specialAccountsTextScreenName")}
						control={control}
						inputProps={{ maxLength: 100 }}
						controllerProps={{ rules: { required: true } }}
						id="id-specialAccount-text-name"
					/>
					<KogInputTextControlledNew
						name="agentKey"
						label={getTranslateByKey("information.specialAccountsTextScreenAgent")}
						control={control}
						controllerProps={{ rules: { required: true } }}
						id="id-specialAccount-text-agentKey"
					/>
					<KogInputAutoCompleteCheckControlled
						name="users"
						label={getTranslateByKey("information.specialAccountsTextScreenUsers")}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						options={usersList || []}
						control={control}
						controllerProps={{
							rules: {
								required: true,
								validate: (value) => {
									return Array.isArray(value) && value.length > 0;
								},
							},
						}}
						customInputProps={{
							required: true,
							id: "id-specialAccount-select-users",
						}}
					/>
					<KogInputPercentageControlled
						name="totalLossPercentage"
						label={getTranslateByKey("information.specialAccountsTextScreenTotalLossPercentage")}
						control={control}
						idScreen="id-specialAccount"
						configRange={{
							rangeNumbers: { min: 0.1, max: 100 },
							decimalPlaces: 2,
						}}
					/>
				</S.AreaForm>
				<KOGAreaButtonsModal>
					<SecondaryButton id="id-specialAccount-button-save" type="submit" isLoading={loadingSave}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
				</KOGAreaButtonsModal>
			</form>
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default SpecialAccountsModal;
